import {
  FormProviderType,
  FormProviderTypeSaveAsTemplate,
  FormProviderTypeUpdateParams,
} from "@/components/forms/form-editor.types.ts";
import {
  useScoreCardFormById,
  useScoreCardFormUpdate,
} from "@/queries/recruit/use-scorecards.query.ts";
import {
  useSurveyFormById,
  useSurveyFormUpdate,
} from "@/queries/surveys/use-survey-forms.query.ts";
import { ApiForm } from "@/types/forms/form-view.types.ts";
import { ApiFormType } from "@/types/forms/form.types.ts";
import { UseMutationResult, UseQueryResult } from "@tanstack/react-query";
import {
  useSurveyTemplateFormById,
  useSurveyTemplateFormUpdate,
} from "@/queries/surveys/use-survey-template-forms.query.ts";
import { useSurveyResetForm } from "@/queries/surveys/use-surveys.query.ts";
import { ApiOperationResult } from "@/types/common.types.ts";
import { ApiFormTemplate } from "@/types/forms/form-template.types.ts";
import { useSurveyTemplateCreateFromSurvey } from "@/queries/surveys/use-survey-templates.query.ts";
import {
  useReviewerTypeFormById,
  useReviewerTypeFormReset,
  useReviewerTypeFormUpdate,
} from "@/queries/performance-reviews/use-reviewer-type-forms.query.ts";
import { usePerformanceReviewTemplateCreateFromReviewerType } from "@/queries/performance-reviews/use-performance-review-template.query.ts";
import { usePerformanceReviewInvalidateSetup } from "@/queries/performance-reviews/use-performance-reviews.query.ts";
import {
  usePerformanceReviewTemplateFormById,
  usePerformanceReviewTemplateFormUpdate,
} from "@/queries/performance-reviews/use-performance-review-template-forms.query.ts";

export enum FormEditorTypes {
  survey = "survey",
  surveyTemplate = "surveyTemplate",
  scoreCard = "scoreCard",
  performanceReview = "performanceReview",
  performanceReviewTemplate = "performanceReviewTemplate",
}

export const formEditorTypes: Record<FormEditorTypes, FormProviderType> = {
  survey: {
    type: ApiFormType.survey,
    templateFolderModule: "surveys",
    hooks: {
      useGetFormById: (id: number): UseQueryResult<ApiForm> => {
        return useSurveyFormById(id);
      },
      useUpdateForm: (): UseMutationResult<void, Error, FormProviderTypeUpdateParams, unknown> => {
        return useSurveyFormUpdate();
      },
      useResetForm: (): UseMutationResult<ApiOperationResult, Error, { id: number }> => {
        return useSurveyResetForm();
      },
      useSaveAsTemplate: (): UseMutationResult<
        ApiFormTemplate,
        Error,
        FormProviderTypeSaveAsTemplate,
        unknown
      > => {
        return useSurveyTemplateCreateFromSurvey();
      },
    },
  },
  surveyTemplate: {
    type: ApiFormType.survey,
    hooks: {
      useGetFormById: (id: number): UseQueryResult<ApiForm> => {
        return useSurveyTemplateFormById(id);
      },
      useUpdateForm: (): UseMutationResult<void, Error, FormProviderTypeUpdateParams, unknown> => {
        return useSurveyTemplateFormUpdate();
      },
      useResetForm: (): undefined => {
        return undefined;
      },
      useSaveAsTemplate: (): undefined => {
        return undefined;
      },
    },
  },
  scoreCard: {
    type: ApiFormType.scoreCard,
    hooks: {
      useGetFormById: (id: number): UseQueryResult<ApiForm> => {
        return useScoreCardFormById(id);
      },
      useUpdateForm: (): UseMutationResult<void, Error, FormProviderTypeUpdateParams, unknown> => {
        return useScoreCardFormUpdate();
      },
      useResetForm: (): undefined => {
        return undefined;
      },
      useSaveAsTemplate: (): undefined => {
        return undefined;
      },
    },
  },
  performanceReview: {
    type: ApiFormType.performanceReview,
    templateFolderModule: "performance-reviews",
    hooks: {
      useGetFormById: (id: number): UseQueryResult<ApiForm> => {
        return useReviewerTypeFormById(id);
      },
      useUpdateForm: (): UseMutationResult<void, Error, FormProviderTypeUpdateParams, unknown> => {
        return useReviewerTypeFormUpdate();
      },
      useResetForm: (): UseMutationResult<ApiOperationResult, Error, { id: number }> => {
        const invalidateSetup = usePerformanceReviewInvalidateSetup();

        return useReviewerTypeFormReset(undefined, {
          onSuccess: () => {
            invalidateSetup();
          },
        });
      },
      useSaveAsTemplate: (): UseMutationResult<
        ApiFormTemplate,
        Error,
        FormProviderTypeSaveAsTemplate
      > => {
        return usePerformanceReviewTemplateCreateFromReviewerType();
      },
    },
  },

  performanceReviewTemplate: {
    type: ApiFormType.performanceReview,
    hooks: {
      useGetFormById: (id: number): UseQueryResult<ApiForm> => {
        return usePerformanceReviewTemplateFormById(id);
      },
      useUpdateForm: (): UseMutationResult<void, Error, FormProviderTypeUpdateParams, unknown> => {
        return usePerformanceReviewTemplateFormUpdate();
      },
      useResetForm: (): undefined => {
        return undefined;
      },
      useSaveAsTemplate: (): undefined => {
        return undefined;
      },
    },
  },
};
